import * as React from "react";
import type { AppUser } from "../../../services/userService";
import type { CascadeResult } from "../detail/linkSlots";
import { BETEILIGTEN_FELDER_OHNE_INFO, NUR_ZU_INFORMIEREN } from "../detail/personenZeile";
import { BeteiligteFeld } from "./BeteiligteFeld";
import { DatumFeld } from "./FristTwvFeld";
import { ZuweisungFeld } from "./ZuweisungFeld";
import { IcoX } from "./vg3Icons";
import { setzeTodoFrist, setzeTodoTwv, todoEntwurfGueltig, type TodoEntwurf } from "./todoEntwurf";
import "./vorgangV3.css";

/**
 * Der Kasten zum Anlegen eines To-dos in der Vorgangskarte (PO 05.09.2026,
 * Zeichnung).
 *
 * Bis hierher nahm die Detailansicht nur einen Betreff entgegen — Zuweisung,
 * Fristen und Beteiligte musste man am fertigen To-do nachtragen. Die
 * Anlage-Maske fragt seit jeher nach allem; die beiden Wege ins selbe Ziel
 * fragten dasselbe verschieden vollständig ab.
 *
 * „Abbrechen" ist nur noch ein ✕ (PO 05.09.2026) — der Kasten trägt drei
 * Knöpfe, und ein ausgeschriebenes „Abbrechen" wog dort so schwer wie das
 * Anlegen selbst.
 */
type Props = {
    entwurf: TodoEntwurf;
    onChange: (next: TodoEntwurf) => void;
    users: AppUser[];
    /** Persönlicher Vorgang: keine Bezüge, keine fremden Beteiligten. */
    persoenlich: boolean;
    busy: boolean;
    onAnlegen: () => void;
    onAbbrechen: () => void;
    /**
     * „Todo eines anderen auswählen" (PO 05.09.2026). Öffnet die Auswahl eines
     * bestehenden To-dos aus einem anderen Vorgang; ohne Handler entfällt der
     * Knopf — im persönlichen Vorgang gibt es nichts zu verknüpfen.
     */
    onAuswaehlen?: () => void;
    /**
     * DER BISHERIGE VORGANG WIRD DAS ERSTE TO-DO (PO 28.09.2026).
     *
     * > „Vorgang ohne To-dos, neues To-do, dann wird der Vorgang erstes To-do
     * > – muss ich dann bei der Erstellung eines To-dos umbenennen … Bei der
     * > Anlage den bisherigen Vorgang, der zu einem To-do wird, umbenennen."
     *
     * Nur gesetzt, solange der Vorgang KEINE To-dos hat. Dann entstehen beim
     * Anlegen zwei To-dos (`planeTodoAnlage`): eines aus dem bisherigen Inhalt,
     * eines für die neue Arbeit. Bis dahin hieß das erste stumm wie der
     * Vorgang und musste hinterher umbenannt werden — jetzt steht sein Name
     * hier, vorbelegt, und lässt sich gleich mit ändern.
     */
    erstesTodo?: { titel: string; vorschlag: string; onChange: (titel: string) => void };
};

export const TodoAnlegenKasten: React.FC<Props> = ({
    entwurf,
    onChange,
    users,
    persoenlich,
    busy,
    onAnlegen,
    onAbbrechen,
    onAuswaehlen,
    erstesTodo,
}) => {
    const setze = (teil: Partial<TodoEntwurf>) => onChange({ ...entwurf, ...teil });

    return (
        <div className="vg3-todo-neu">
            {erstesTodo && (
                <div className="vg3-todo-neu-erstes">
                    <span className="vg3-leer">
                        Der Vorgang hat noch keine To-dos. Sein bisheriger Inhalt wird zum ersten To-do:
                    </span>
                    <input
                        className="vg3-todo-neu-betreff"
                        placeholder={erstesTodo.vorschlag || "Vorgang"}
                        aria-label="Name des ersten To-dos aus dem bisherigen Vorgang"
                        value={erstesTodo.titel}
                        disabled={busy}
                        onChange={(e) => erstesTodo.onChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") onAbbrechen();
                        }}
                    />
                    <span className="vg3-leer">Neues To-do:</span>
                </div>
            )}
            <div className="vg3-todo-neu-kopf">
                <input
                    autoFocus
                    className="vg3-todo-neu-betreff"
                    placeholder="Betreff des To-dos…"
                    aria-label="Betreff des neuen To-dos"
                    value={entwurf.titel}
                    onChange={(e) => setze({ titel: e.target.value })}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && todoEntwurfGueltig(entwurf)) onAnlegen();
                        if (e.key === "Escape") onAbbrechen();
                    }}
                />
                <button
                    type="button"
                    className="vg3-ibtn"
                    title="Abbrechen"
                    aria-label="Abbrechen"
                    onClick={onAbbrechen}
                >
                    <IcoX size={16} />
                </button>
            </div>

            <div className="vg3-todo-neu-zeile">
                <ZuweisungFeld
                    slots={entwurf.slots}
                    canEdit={!persoenlich}
                    persoenlich={persoenlich}
                    actionLabel="Kontakt, Risiko, Vertrag hinzufügen"
                    // Nur die Slots übernehmen: der `patch` gehört zu einem
                    // Datensatz, den es noch gar nicht gibt.
                    onApply={(r: CascadeResult) => setze({ slots: r.slots })}
                />
            </div>

            <div className="vg3-todo-neu-zeile">
                <span className="vg3-frist-label">TWV:</span>
                <DatumFeld
                    label="Wiedervorlage des neuen To-dos"
                    value={entwurf.twv}
                    canEdit
                    onSave={(v) => onChange(setzeTodoTwv(entwurf, v))}
                />
                <span className="vg3-frist-strich" aria-hidden>|</span>
                <span className="vg3-frist-label">Frist:</span>
                <DatumFeld
                    label="Frist des neuen To-dos"
                    value={entwurf.frist}
                    canEdit
                    onSave={(v) => onChange(setzeTodoFrist(entwurf, v))}
                />
            </div>

            {!persoenlich && (
                <div className="vg3-todo-neu-zeile">
                    <span className="vg3-zeile-label">Beteiligte</span>
                    <BeteiligteFeld
                        users={users}
                        canEdit
                        gruppen={BETEILIGTEN_FELDER_OHNE_INFO}
                        werte={{
                            hauptverantwortlich: entwurf.hauptverantwortlich,
                            bearbeiter: entwurf.bearbeiter,
                            zuInformierende: entwurf.zuInformierende,
                        }}
                        onChange={(g, next) => setze({ [g]: next } as Partial<TodoEntwurf>)}
                        popoverTitel="Beteiligte"
                    />

                    <span className="vg3-zeile-label vg3-zeile-weiter">Zu informieren</span>
                    <BeteiligteFeld
                        users={users}
                        canEdit
                        gruppen={NUR_ZU_INFORMIEREN}
                        werte={{
                            hauptverantwortlich: entwurf.hauptverantwortlich,
                            bearbeiter: entwurf.bearbeiter,
                            zuInformierende: entwurf.zuInformierende,
                        }}
                        onChange={(g, next) => setze({ [g]: next } as Partial<TodoEntwurf>)}
                        leerLabel="hinzufügen"
                        sucheBeiLeerOffen
                    />
                </div>
            )}

            <div className="vg3-todo-neu-akt">
                <button
                    type="button"
                    className="vg3-btn primary"
                    style={{ marginLeft: 0 }}
                    disabled={busy || !todoEntwurfGueltig(entwurf)}
                    onClick={onAnlegen}
                >
                    {busy ? "…" : "Anlegen"}
                </button>

                {onAuswaehlen && (
                    <button type="button" className="vg3-btn" disabled={busy} onClick={onAuswaehlen}>
                        Auswählen
                    </button>
                )}
                {onAuswaehlen && (
                    <span className="vg3-leer">oder ein To-do eines anderen Vorgangs</span>
                )}
            </div>
        </div>
    );
};
